/**
 * Was ein Gerät über sich meldet, als Zeilen für die Detailseite.
 *
 * **Hier und nicht in der Komponente**, weil es reine Rechnung ist: aus einem
 * Objekt wird eine Liste aus Bezeichnung und Wert. Getestet wird in dieser App
 * genau das — die Oberfläche selbst prüft Playwright (HM-022), und ein Test,
 * der Komponenten in einer nachgebauten DOM-Umgebung anklickt, prüft vor allem
 * die Nachbildung.
 *
 * **Der Anlass war eine weiße Seite.** Die Zeilenbildung stand in der
 * Komponente und begann mit `Object.entries(device.reportedSettings)`. Gegen
 * eine API, die das Feld noch nicht kennt, ist das `undefined` — und der
 * `TypeError` daraus nahm nicht die Karte mit, sondern die **ganze Seite**. Für
 * eine Anzeige, die im Normalfall ohnehin leer ist, ist das der denkbar
 * schlechteste Tausch. Deshalb nehmen alle Funktionen `null` und `undefined`
 * entgegen und antworten mit einer leeren Liste: „nicht gemeldet" und „kennt
 * dieses Feld nicht" sind an dieser Stelle dieselbe Aussage.
 *
 * **Keine Funktion gibt je eine Zeile mit Platzhalter zurück.** Was das Gerät
 * nicht gemeldet hat, fehlt — es steht nicht als „nicht gemeldet" da. Ein
 * Platzhalter sieht aus wie eine Auskunft und ist keine; auf einer Seite, die
 * bei einem frisch gekoppelten Gerät fast nur aus solchen Zeilen bestünde, ist
 * er die unehrlichste Form von Anzeige. Ob eine Karte überhaupt erscheint,
 * entscheidet deshalb die Länge ihrer Liste.
 *
 * **Gruppiert wird nach der Frage, nicht nach der Nachrichtenart.** Die
 * Firmware kommt aus `hello`, das Update-Kennzeichen aus `diag` — beide
 * beantworten „welcher Stand läuft da". Wer stattdessen je Nachrichtenart eine
 * Karte baut, verteilt zusammengehörige Angaben auf zwei Kästen und zwingt zum
 * Springen.
 */
import type { ApiComponents } from "@hausmeter/sdk";
import {
  REACHABILITY_LABELS,
  REACHABILITY_TONES,
  SETTING_LABELS,
  UNLINK_REASON_LABELS,
} from "./device-labels.js";
import { deviceClass, remoteWritableKeys } from "./device-profile.js";
import { formatDateTime, formatTimeAgo, formatUptime } from "./format.js";
import type { DeviceTab, Route } from "./router.js";

type Device = ApiComponents["schemas"]["Device"];
type GatewayNode = ApiComponents["schemas"]["GatewayNode"];
type IntakeStatus = ApiComponents["schemas"]["IntakeStatusResponse"];
type ReportedSettings = NonNullable<Device["reportedSettings"]>;
type Diagnostics = NonNullable<Device["diagnostics"]>;

/** Bezeichnung links, Wert rechts — die Form, die `DefinitionList` erwartet. */
export type Row = readonly [string, string];

/** Sammelt Zeilen und überspringt dabei alles, was nicht gemeldet wurde. */
function collector(): { add: (label: string, value: string | undefined) => void; rows: Row[] } {
  const rows: Row[] = [];
  return {
    rows,
    add: (label, value) => {
      if (value !== undefined) rows.push([label, value]);
    },
  };
}

/**
 * Die gemeldete Konfiguration als Zeilen.
 *
 * **Nur was gemeldet wurde.** Ein Schlüssel, den diese Firmware nicht kennt,
 * fehlt in der Liste, statt als „–" dazustehen: Das eine heißt „hat es nicht
 * gesagt", das andere hieße „hat nichts eingestellt".
 *
 * **Der Gerätename fehlt hier**, obwohl er ein Konfigurationsschlüssel ist: Er
 * steht seit HM-041 als Überschrift der Seite, weil der Ingest ihn als
 * `devices.name` übernimmt. Eine Zeile „Gerätename" darunter zeigte dasselbe
 * Wort ein zweites Mal — dieselbe Überlegung wie bei Typ und Seriennummer, die
 * in `hardwareRows` ebenfalls fehlen.
 */
export function settingRows(settings: ReportedSettings | null | undefined): readonly Row[] {
  if (!settings) return [];

  return (Object.entries(settings) as [keyof ReportedSettings, unknown][])
    // Das Ende der Diagnosefreigabe ist eine Unix-Zeit; gedeutet steht es in
    // `Device.remoteAccess` und wird dort gezeigt (#105), nicht als nackte Zahl.
    .filter(
      ([key, value]) =>
        value !== undefined &&
        key !== "deviceName" &&
        key !== "cloudDiagUntil" &&
        key in SETTING_LABELS,
    )
    .map(([key, value]) => [SETTING_LABELS[key], settingValue(value)] as const);
}

/**
 * Was für ein Gerät das ist — Bauteil und Stand.
 *
 * **Typ und Seriennummer stehen hier nicht**, obwohl sie technische Daten sind:
 * Sie stehen in der Kopfzeile, direkt unter dem Namen, und eine Karte, die sie
 * wiederholt, verschiebt nur den Blick.
 *
 * Die Firmware kommt aus zwei Richtungen: aus `hello` beim Verbinden und aus
 * der Diagnose als laufender Stand. Der Wert aus der Diagnose geht vor, weil er
 * jünger ist — beide meinen dieselbe Zahl.
 *
 * **Die MAC-Adresse steht nur da, wenn sie jemand geliefert hat.** Über die
 * Cloud kommt sie heute nie: Das Gerät meldet sie in keiner Nachricht, und nur
 * die lokale Erkennung (`cts_local`) kennt sie. Als Zeile mit „nicht gemeldet"
 * stünde sie an jedem Cloud-Gerät und sähe aus wie ein Fehler.
 */
export function hardwareRows(
  device: Pick<Device, "hardwareVersion" | "firmwareVersion" | "macAddress">,
  diag: Diagnostics | null | undefined,
): readonly Row[] {
  const { add, rows } = collector();

  add("Hardware", device.hardwareVersion ?? undefined);
  add("Firmware", diag?.fw?.running ?? device.firmwareVersion ?? undefined);
  add("Update verfügbar", booleanOrNothing(diag?.fw?.updateAvailable, "ja", "nein"));
  add("MAC-Adresse", device.macAddress ?? undefined);

  return rows;
}

/**
 * Wie das Gerät im Netz hängt — die Frage, mit der jemand hierherkommt, wenn
 * nichts ankommt.
 *
 * Hier stehen WLAN-Name und lokale IP-Adresse, die heikelsten Angaben des
 * ganzen Protokolls. Sie sind der Grund, warum ein Gerät nicht erreichbar ist,
 * und ohne sie ist Ferndiagnose Raten; gespeichert wird deshalb nur der letzte
 * Stand und keine Reihe (`docs/compliance/data-protection.md`).
 *
 * **Die Feldstärke kommt aus zwei Nachrichten und steht einmal da.** `status`
 * schreibt sie an das Gerät (`signalStrengthDbm`), `diag` trägt sie im
 * Netzblock; es ist dieselbe Größe, und zwei Zeilen mit verschiedenen Zahlen
 * ließen einen nach dem Unterschied suchen, den es nicht gibt.
 */
export function networkRows(
  device: Pick<Device, "signalStrengthDbm">,
  diag: Diagnostics | null | undefined,
): readonly Row[] {
  const { add, rows } = collector();

  add("WLAN", diag?.net?.ssid);
  add("Lokale IP-Adresse", diag?.net?.ip);
  add("Funksignal", signalLabel(device.signalStrengthDbm ?? diag?.net?.rssi));
  add("Verbindungsabbrüche", numberOrNothing(diag?.net?.disconnects));

  return rows;
}

/**
 * Wie lange und wie stabil das Gerät läuft.
 *
 * Der Füllstand der Zwischenspeicher gehört hierher und nicht zu den Zählern: Er
 * beschreibt das Gerät, nicht den Lesekopf.
 *
 * **Was er bedeutet, steht dahin — deshalb „belegt" und nicht „offen"**
 * (HM-050, OQ-030). Das Protokoll sagt zu, es sei der Stand des
 * *Cloud-Lesezeigers*, also der Rückstand (`docs/device/mqtt-protocol.md`).
 * Gemessen liefert das erste echte Gerät aber `288 von 288` **und** `96 von 96`
 * — beide Ringe exakt voll, während im Minutentakt Werte ankommen. Entweder
 * meldet die Firmware belegte Ringplätze statt des Rückstands, oder hier steht
 * ein Stau, den nichts anderes auf der Seite bestätigt. Solange das offen ist,
 * gibt die Zeile die Zahl wieder und deutet sie nicht (Regel 9) — und es gibt
 * **keinen** Warnhinweis dazu: Bei der falschen Lesart stünde er an jedem Gerät
 * dauerhaft und entwertete die Hinweise daneben.
 */
export function systemRows(
  diag: Diagnostics | null | undefined,
  uptime?: { readonly seconds: number | null; readonly at: string | null },
  now?: number,
): readonly Row[] {
  const { add, rows } = collector();

  /**
   * **Die Laufzeit trägt hier ihren Zeitbezug, oben in der Kachel nicht.**
   * Sie kommt aus `status` und damit aus einer jüngeren Nachricht als der Rest
   * dieser Karte — der Untertitel „Momentaufnahme von …" gilt für sie also
   * nicht. Und sie ist die einzige Angabe der Seite, die schon im Augenblick des
   * Lesens nicht mehr stimmt: Ohne „gemeldet vor …" ist „20 Stunden" an einem
   * stillen Gerät eine Behauptung über die Gegenwart.
   */
  add(
    "Laufzeit",
    uptime?.seconds == null
      ? undefined
      : `${formatDuration(uptime.seconds)}` +
          (uptime.at === null ? "" : ` · gemeldet ${formatTimeAgo(uptime.at, now)}`),
  );
  add("Letzter Start", diag?.sys?.resetReason);
  add("Startzähler", numberOrNothing(diag?.sys?.bootCount));
  add(
    "Freier Speicher",
    diag?.sys?.heapFree === undefined ? undefined : `${Math.round(diag.sys.heapFree / 1024)} KiB`,
  );

  /**
   * **`pending` ist der Rückstand, nicht die Belegung** (OQ-030, seit der Auskunft
   * in `docs/device/backfill-answer.md` entschieden). Die Firmware setzt den
   * Lesezeiger eines rückstandsfreien Abnehmers sofort ans Ende; im Normalbetrieb
   * steht hier deshalb eine Null, und jede andere Zahl heißt „so viele Fenster
   * hat die Cloud noch nicht". „Belegt" las sich wie ein Füllstand und ließ ein
   * gesundes Gerät aussehen, als sammle sich etwas an.
   *
   * Die Kapazität steht daneben, weil sie **nicht** zugesichert ist: Die Ringe
   * werden beim Start an den freien Speicher angepasst. Ein Rückstand, der sie
   * erreicht, ist der Beginn von Datenverlust.
   */
  for (const [name, buffer] of Object.entries(diag?.buffers ?? {})) {
    const pending = buffer.pending ?? 0;
    rows.push([
      `Zwischenspeicher ${name}`,
      pending === 0
        ? `nichts nachzusenden · ${buffer.capacity ?? 0} Fenster Reichweite`
        : `${pending} von ${buffer.capacity ?? 0} Fenstern nachzusenden`,
    ]);
  }

  return rows;
}

/**
 * Was die Leseköpfe empfangen — eine eigene Gruppe und keine Zeilen am Ende der
 * Diagnose.
 *
 * Es ist die einzige Angabe der Diagnose, die etwas über die **Messung** sagt;
 * alles andere beschreibt das Gerät. Zwischen „freier Speicher" und „Zähler
 * ch0" liegt der Unterschied zwischen „läuft das Gerät" und „liest es etwas",
 * und das ist die Grenze, an der man beim Suchen abbiegt.
 */
export function meterRows(diag: Diagnostics | null | undefined): readonly Row[] {
  return (diag?.meters ?? []).map(
    (meter) => [`Zähler ${meter.ch ?? "?"}`, meterDiagnosis(meter)] as const,
  );
}

/**
 * Woher der Name dieses Geräts kommt — und damit, wo man ihn ändert.
 *
 * **Drei Fälle, weil es drei Wege gibt.** Der Kopf der Seite hatte bis hierher
 * nur zwei und schickte deshalb an den falschen Ort: Meldete ein Gerät seinen
 * Namen, stand dort „Ändern lässt er sich an seiner eigenen Oberfläche" — und
 * unter „Einstellungen" lag ein Feld „Gerätename", das genau das aus der Ferne tut.
 * Wer den Satz las, fuhr ins Heimnetz für etwas, das eine Handbreit daneben ging.
 *
 * Die beiden Wege sind nicht dasselbe und dürfen es nicht sein:
 *
 * - `user` — **kein** gemeldeter Name. `PATCH /devices/{id}` ist die einzige
 *   Quelle, der Umbenennen-Knopf steht.
 * - `device_remote` — das Gerät benennt sich selbst **und** nimmt `deviceName`
 *   als Auftrag entgegen. Der Knopf bleibt weg (der Ingest überschriebe den hier
 *   vergebenen Namen beim nächsten Verbindungsaufbau), aber der Weg führt in die
 *   Konfiguration, wo der Auftrag hinausgeht und das Gerät ihn zurückmeldet.
 * - `device_local` — es benennt sich selbst und lässt sich von hier nicht
 *   umstellen. Erst dann stimmt der Verweis auf die Oberfläche des Geräts.
 *
 * Ob `deviceName` fernsetzbar ist, beantwortet `canSetRemotely` — **dieselbe
 * Funktion, die das Konfigurationsformular benutzt**. Zwei Prüfungen wären zwei
 * Gelegenheiten, verschieden streng zu sein, und genau daraus ist der
 * Widerspruch entstanden.
 */
export type NameSource = "user" | "device_remote" | "device_local";

export function nameSource(device: Device): NameSource {
  const reported = (device.reportedSettings?.deviceName ?? "").trim();
  if (reported.length === 0) return "user";

  /* Aus der Geräteklasse, nicht aus `capabilities` allein (HM-113): Ein
     Gateway ohne gemeldete Fähigkeiten nimmt **keinen** Namen entgegen — mit
     `null` als „setzbar" verwiese diese Seite auf ein Feld, das es dort nicht
     gibt. */
  return canSetRemotely("deviceName", remoteWritableKeys(device))
    ? "device_remote"
    : "device_local";
}

/**
 * Ob sich ein Konfigurationsschlüssel von hier aus setzen lässt.
 *
 * **`null` heißt setzbar.** Es bedeutet „das Gerät hat nichts über seine
 * Fähigkeiten gesagt", nicht „es kann nichts" — ein Feld deswegen auszublenden
 * hieße, aus Schweigen ein Verbot abzuleiten (Regel 9). Und **kein Schutz**: Der
 * liegt am Gerät, das einen unerlaubten Auftrag mit `denied` quittiert.
 */
export function canSetRemotely(key: string, writable: readonly string[] | null): boolean {
  return writable === null || writable.includes(key);
}

/* --------------------------------------------------------------------------
 * Die Kachelreihe und die Hinweise
 * ----------------------------------------------------------------------- */

/** Die Marke am Gerätenamen: ein Wort und ein Ton. */
export type ReachabilityBadge = {
  readonly label: string;
  readonly tone: "success" | "warning" | "danger" | "neutral";
  /** Warum es so aussieht — für die Maus, nicht als zweite Aussage. */
  readonly title?: string;
};

/**
 * Wie das Gerät an seinem Namen markiert wird.
 *
 * **Ein Gerät im reinen Lokalbetrieb bekommt keine Erreichbarkeitsmarke.** Es
 * sendet der Cloud nie etwas; „nicht erreichbar" wäre dort keine Störung,
 * sondern die Beschreibung seiner Betriebsart — eine rote Marke, die sich nie
 * abstellen lässt, und nach der zweiten liest niemand mehr die daneben. Es steht
 * deshalb neutral „nur lokal" da, und den Rest erklärt der Hinweis.
 *
 * Das ist die **einzige** Stelle, an der die Betriebsart in dieser Anwendung noch
 * vorkommt: Sie ist eine Angabe für die Mobile-App, die zwischen Cloud und Gerät
 * im LAN wählt. Hier wäre sie an jedem anderen Gerät dasselbe Wort.
 */
export function reachabilityBadge(device: Device): ReachabilityBadge {
  if (device.operatingMode === "local_only") {
    return { label: "nur lokal", tone: "neutral", title: "Sendet nichts an die Cloud" };
  }
  /* Erreichbar und trotzdem ohne Werte (HM-179): In der Liste stünde sonst
     „erreichbar" in Grün an einem Gerät, dessen Zähler seit Stunden fehlt. Gelb
     wie „überfällig" — es lohnt der Blick, kaputt ist nichts nachweislich. */
  if (device.reachability === "reachable" && device.readingsStalled) {
    return { label: "keine Werte", tone: "warning", title: "Meldet sich, liefert aber keine Werte" };
  }
  return {
    label: REACHABILITY_LABELS[device.reachability],
    tone: REACHABILITY_TONES[device.reachability],
    title: device.reachability,
  };
}

/** Die Marke „Update verfügbar" — Beschriftung, Ton und die Herkunft der Aussage. */
export type UpdateBadge = {
  readonly label: string;
  readonly tone: "info";
  readonly title: string;
};

/**
 * Ob dieses Gerät ein Firmware-Update gemeldet hat.
 *
 * **Nur `true` ergibt eine Marke.** `false` heißt „es gibt keins" und braucht
 * keine, `undefined` heißt „das Gerät hat dazu nichts gesagt" — und aus
 * Schweigen eine Aussage zu machen wäre Raten (Regel 9). Ein Gerät im reinen
 * Lokalbetrieb schickt der Cloud gar keine Diagnose und fällt damit von selbst
 * heraus; ein Sonderfall dafür wäre einer zu viel.
 *
 * **Der Ton ist `info` und nicht `warning`.** Ein verfügbares Update ist nichts,
 * was hakt — es steht in derselben Zeile wie „überfällig" und „nicht
 * erreichbar", und wenn beides gleich aussieht, sagt keines von beiden mehr
 * etwas. Dass die Firmware-Kachel der Detailseite dieselbe Angabe gelb trägt,
 * ist kein Widerspruch, sondern der Wortschatz dort: Eine Kachel kennt nur
 * `ok` und `warn`, sonst ließe sich die Angabe gar nicht hervorheben.
 *
 * **Und der Titel nennt, wann es gemeldet wurde.** Eine Diagnose ist eine
 * Momentaufnahme vom letzten Verbindungsaufbau (`docs/device/`); ein Gerät, das
 * sich inzwischen selbst aktualisiert hat, widerruft sie erst beim nächsten.
 * Ohne den Zeitpunkt stünde hier eine Behauptung ohne Alter.
 */
export function updateBadge(device: Device, now?: number): UpdateBadge | null {
  if (device.diagnostics?.fw?.updateAvailable !== true) return null;

  const running = device.diagnostics.fw.running ?? device.firmwareVersion;
  // Beide Zusätze fallen weg statt „noch keine Daten" zu schreiben: In einem
  // Titel steht sonst ein halber Satz, der aussieht wie ein Fehler.
  const laufend = running === null || running === undefined ? "" : ` als die laufende ${running}`;
  const gemeldet =
    device.diagnosticsAt === null ? "" : ` — gemeldet ${formatTimeAgo(device.diagnosticsAt, now)}`;

  return {
    label: "Update verfügbar",
    tone: "info",
    title: `Das Gerät meldet eine neuere Firmware${laufend}${gemeldet}.`,
  };
}

/**
 * Die Marke, wenn der Ingest Nachrichten dieses Geräts verwirft (HM-103).
 *
 * **Warnfarbe, nicht Rot.** Das Gerät ist erreichbar und sendet — zu viel. Was
 * durchkommt, wird geschrieben; was verworfen wird, sind die Nachrichten über
 * dem Vorrat. Der Titel nennt den Beginn, weil ein Wackelkontakt, der seit
 * drei Tagen jede Sekunde ein Telegramm auslöst, eine andere Lage ist als einer
 * von eben.
 */
export type FloodingBadge = {
  readonly label: string;
  readonly tone: "warning";
  readonly title: string;
};

export function floodingBadge(device: Device, now?: number): FloodingBadge | null {
  if (device.floodingSince === null) return null;
  return {
    label: "Sendet zu viel",
    tone: "warning",
    title:
      `Das Gerät überschreitet seit ${formatTimeAgo(device.floodingSince, now)} sein ` +
      "Nachrichtenkontingent; überzählige Nachrichten werden verworfen.",
  };
}

/**
 * Eine der vier Kacheln unter der Kopfkarte.
 *
 * `tone` ist die Einordnung und **keine Größenfarbe** (ADR 0013): Sie sagt
 * „daran hakt etwas", nicht „das ist viel".
 */
export type Tile = {
  readonly label: string;
  readonly value: string;
  readonly note?: string;
  readonly tone: "ok" | "warn";
};

/**
 * Wie oft ein Zähler ausgelesen wird, der abgefragt werden muss — ein Wärmemengenzähler
 * (`readInterval`, Sekunden). **Er ersetzt den Sendetakt in der Kachel:** Ein WMZ meldet sich
 * alle fünf Minuten, liest aber nur alle zwölf Stunden aus; „Sendetakt 60 s" gäbe an, was er
 * liest, nicht was er sendet. `"0"` heißt: nur auf Anforderung. Was keine Zahl ist, sagt nichts.
 */
export function readIntervalNote(readInterval: string | undefined): string | undefined {
  if (readInterval === undefined) return undefined;
  const seconds = Number(readInterval);
  if (!Number.isFinite(seconds) || seconds < 0) return undefined;
  if (seconds === 0) return "Auslesung nur auf Anforderung";
  if (seconds % 3600 === 0) return `Auslesung alle ${seconds / 3600} Std.`;
  if (seconds % 60 === 0) return `Auslesung alle ${seconds / 60} Min.`;
  return `Auslesung alle ${seconds} s`;
}

/**
 * Die Kachelreihe: die vier Fragen, die man einem Gerät zuerst stellt.
 *
 * **Eine Kachel ohne Wert entsteht gar nicht.** Der Entwurf schreibt an ihrer
 * Stelle `—`; an einem frisch gekoppelten Gerät stünden damit drei Striche
 * nebeneinander, und das sieht aus wie ein Schaden. Übrig bleibt dann die eine
 * Kachel, die immer etwas zu sagen hat — auch „noch keine Daten" ist eine
 * Auskunft, und zwar die wichtigste, solange sie gilt.
 */
export function statTiles(device: Device, now?: number, gateway?: Device | null): readonly Tile[] {
  const diag = device.diagnostics;
  const tiles: Tile[] = [];

  const interval = device.reportedSettings?.cloudInterval;

  tiles.push({
    label: "Letzte Meldung",
    value: formatTimeAgo(device.lastSeenAt, now),
    note: readIntervalNote(device.reportedSettings?.readInterval) ?? (interval === undefined ? undefined : `Sendetakt ${interval} s`),
    /* Grün nur, solange das Gerät auch wirklich sendet. `never_seen` bleibt
       ruhig: Ein frisch gekoppeltes Gerät ist kein Fall für eine Warnfarbe. */
    tone: isSilent(device) ? "warn" : "ok",
  });

  /**
   * **Ein Gerät hinter dem Gateway hat seinen Weg in einer eigenen Kachel**
   * (HM-113). Funk, Laufzeit und Firmware meldet es nicht — es hat keine
   * Verbindung, über die es sie melden könnte —, und die Frage, die an ihrer
   * Stelle steht, lautet: Liegt ein stilles Gerät an sich selbst oder am
   * Gateway? Die Kachel färbt sich deshalb am **Gateway**, nicht am Knoten.
   */
  if (deviceClass(device) === "gateway_node" && gateway !== undefined && gateway !== null) {
    tiles.push({
      label: "Über Gateway",
      value: gateway.name,
      note: REACHABILITY_LABELS[gateway.reachability],
      tone: isSilent(gateway) ? "warn" : "ok",
    });
  }

  const dbm = device.signalStrengthDbm ?? diag?.net?.rssi;
  if (dbm !== null && dbm !== undefined) {
    tiles.push({
      label: "Funksignal",
      value: `${dbm} dBm`,
      note: signalQuality(dbm),
      tone: signalQuality(dbm) === "schwach" ? "warn" : "ok",
    });
  }

  const uptime = device.uptimeSeconds;
  if (uptime !== null) {
    const drops = diag?.net?.disconnects;
    tiles.push({
      label: "Laufzeit",
      value: formatUptime(uptime),
      note: drops === undefined ? undefined : `${drops} ${drops === 1 ? "Abbruch" : "Abbrüche"}`,
      tone: "ok",
    });
  }

  const firmware = diag?.fw?.running ?? device.firmwareVersion;
  if (firmware !== null && firmware !== undefined) {
    const update = diag?.fw?.updateAvailable;
    tiles.push({
      label: "Firmware",
      value: firmware,
      // `undefined` bleibt leer: Ein Gerät, das nichts über Updates gesagt hat,
      // hat nicht gesagt, dass es keine gibt (Regel 9).
      note: booleanOrNothing(update, "Update verfügbar", "kein Update"),
      tone: update === true ? "warn" : "ok",
    });
  }

  return tiles;
}

/**
 * Ein Hinweis über den Reitern — was an diesem Gerät gerade auffällt.
 *
 * `go` führt zu dem Reiter, auf dem sich etwas tun lässt. Ein Hinweis **ohne**
 * `go` ist kein unfertiger: Gegen ein schwaches Funksignal hilft ein näherer
 * Zugangspunkt und kein Knopf in dieser Anwendung.
 */
export type Hint = {
  readonly id: string;
  readonly title: string;
  readonly text: string;
  readonly go?: DeviceTab;
  /**
   * Eine **andere** Adresse statt eines Reiters — seit HM-113 das Gateway, wenn
   * es der Grund ist, warum ein Gerät dahinter schweigt. Ein Link, kein Knopf:
   * Es ist ein Ort, und den öffnet man auch im neuen Tab.
   */
  readonly route?: Route;
  readonly action?: string;
};

/**
 * Der Hinweis, wenn die Annahmestelle gerade nichts entgegennimmt (HM-056).
 *
 * **Exportiert, weil ihn zwei Ansichten brauchen** — die Geräteverwaltung über
 * ihrer Liste und die Detailseite über ihren Reitern. Zwei Formulierungen für
 * denselben Zustand wären zwei Gelegenheiten, ihn verschieden zu erklären.
 *
 * **`unknown` bekommt keinen Hinweis.** Es heißt, dass diese Anwendung den
 * Zustand nicht kennt — der Broker ist für sie nicht erreichbar, oder es liegt
 * noch keine Meldung vor. Daraus eine Störung zu machen wäre Raten (Regel 9),
 * und ein Kasten, der bei jedem Neustart der API kurz aufblitzt, ist schlimmer
 * als keiner.
 */
export function intakeNotice(intake: IntakeStatus | null | undefined, now?: number): Hint | null {
  if (intake?.state !== "not_accepting") return null;
  return intakeHint(intake, now);
}

function intakeHint(intake: IntakeStatus, now?: number): Hint {
  const seit = intake.since === null ? "" : ` — seit ${formatTimeAgo(intake.since, now)}`;
  return {
    id: "intake-stopped",
    title: "Die Datenannahme steht still — nicht dein Gerät",
    text:
      `Der Dienst, der Gerätedaten entgegennimmt, meldet, dass er gerade nichts annehmen ` +
      `kann${seit}${intake.reason === null ? "" : ` (${intake.reason})`}. Deine Geräte messen ` +
      "weiter und halten die Werte zurück; sie liefern sie nach, sobald die Annahme wieder " +
      "läuft. Angaben zu Erreichbarkeit und letzter Meldung stehen so lange still — ein " +
      "Neustart des Geräts ändert daran nichts.",
  };
}

/**
 * Woran es gerade hakt — die gelben Kästen zwischen Kacheln und Reitern.
 *
 * **Jeder Hinweis muss verschwinden können.** Das ist die Bedingung, an der
 * einer aus dem Entwurf gescheitert ist: „Kanäle ohne Zähler" stünde an **jedem**
 * Gerät dauerhaft — ein Warnkasten, der sich nie abstellen lässt, wird nach dem
 * zweiten Mal nicht mehr gelesen und entwertet die beiden daneben. Seit HM-045
 * gibt es die Aussage ohnehin nicht mehr: Ein Zähler hängt nicht am Kanal,
 * sondern über eine Bindung am Zählwerk.
 *
 * **„Nicht erreichbar" nur, wenn es das je war.** Ein frisch gekoppeltes Gerät
 * hat sich noch nie gemeldet, ohne dass etwas fehlschlägt; der Satz „hat sich
 * zuletzt … gemeldet" hätte dort keine Zeit einzusetzen. Das erledigt seit der
 * Abstufung der Zustand selbst: `never_seen` bekommt keinen Hinweis, und die
 * Erklärung steht unter den Auskunftskarten, wo sie hingehört.
 */
export function deviceHints(
  device: Device,
  now?: number,
  intake?: IntakeStatus | null,
  /**
   * Das Gateway eines Geräts dahinter (HM-113) — `undefined`, solange es nicht
   * geladen ist, und dann behauptet kein Hinweis etwas über seinen Zustand.
   */
  gateway?: Device | null,
): readonly Hint[] {
  const hints: Hint[] = [];
  const kind = deviceClass(device);

  /**
   * **Steht die Annahme still, ist jede Aussage über die Erreichbarkeit
   * eingefroren** — und zwar für alle Geräte gleichzeitig. `last_seen_at`
   * altert, weil niemand ein Lebenszeichen entgegennimmt; die Marke springt
   * nach der Frist auf „überfällig", obwohl das Gerät sendet und puffert.
   *
   * Deshalb steht dieser Hinweis **vor** allen anderen und **ersetzt** den zur
   * Erreichbarkeit: Beide nebeneinander wären zwei Erklärungen für eine
   * Ursache, und die untere riete zu einem Neustart des Geräts, der nichts
   * bewirkt.
   *
   * Ein Gerät im reinen Lokalbetrieb bleibt davon unberührt — es sendet ohnehin
   * nicht an die Cloud, und der Zweig dafür steht weiter unten.
   */
  const intakeStopped = intake?.state === "not_accepting" && device.operatingMode !== "local_only";
  if (intakeStopped) {
    hints.push(intakeHint(intake, now));
  }

  if (device.floodingSince !== null) {
    hints.push(floodingHint(device, kind, now));
  }
  if (device.lastFlood !== null) {
    hints.push(pastFloodHint(device.lastFlood, kind));
  }

  if (device.unlinkReportedAt) {
    hints.push({
      id: "unlink",
      title: "Dieses Gerät hat sich abgemeldet",
      text:
        `Es hat ${formatTimeAgo(device.unlinkReportedAt, now)} gemeldet, dass es ` +
        `${device.unlinkReason === null ? "getrennt wurde" : UNLINK_REASON_LABELS[device.unlinkReason]} ` +
        "— und danach seine Zugangsdaten gelöscht. Es sendet nichts mehr und kommt auch nicht " +
        "wieder herein. Der Platz in dieser Anlage bleibt trotzdem belegt: Gib es frei, " +
        "wenn es dauerhaft weg ist, oder kopple es erneut, wenn es zurückkommen soll.",
      go: "settings",
      action: "Zu den Einstellungen",
    });
  }

  /**
   * **Ein Gerät im reinen Lokalbetrieb ist hier immer still, und das ist keine
   * Störung.** Ohne diesen Satz stünde es unerklärt neben lauter Geräten, die
   * senden — die Betriebsart steht in dieser Anwendung sonst nirgends mehr, weil
   * sie eine Angabe für die Mobile-App ist. Ein Hinweis auf die Erreichbarkeit
   * erübrigt sich damit; deshalb steht dieser Zweig vor den beiden anderen.
   */
  if (device.operatingMode === "local_only") {
    hints.push({
      id: "local-only",
      title: "Dieses Gerät sendet nichts an die Cloud",
      text:
        "Es ist auf reinen Lokalbetrieb eingestellt und meldet sich weder hier noch an einer " +
        "Datenquelle. Seine Werte stehen im Heimnetz zur Verfügung — in dieser Ansicht bleibt " +
        "es dauerhaft ohne Meldung, und das ist kein Fehler.",
    });
  } else if (intakeStopped) {
    // Der Hinweis oben sagt es bereits, und er sagt es richtig: Die Angaben zur
    // Erreichbarkeit stammen aus einer Zeit, in der noch jemand zuhörte.
  } else if (kind === "gateway_node") {
    const hint = nodeSilenceHint(device, gateway, now);
    if (hint !== null) hints.push(hint);
  } else if (device.reachability === "unreachable") {
    hints.push({
      id: "offline",
      title: "Nicht erreichbar",
      text:
        `Die Verbindung ist abgerissen; zuletzt gemeldet hat sich das Gerät ` +
        `${formatTimeAgo(device.lastSeenAt, now)}. ` +
        (kind === "gateway"
          ? "Solange sie fehlt, kommt auch von den Geräten dahinter nichts an — sie haben keine " +
            "eigene Verbindung."
          : "Ohne Verbindung misst es weiter und legt die Werte in seinen Ringpuffer; sichtbar " +
            "werden sie erst nach der Rückkehr."),
      /* Unter „Einstellungen" liegen die Aktionen — beim Gateway gibt es dort
         keine, und ein Knopf, der auf eine Seite ohne Handlung führt, ist ein
         Umweg (HM-113). */
      ...(kind === "gateway" ? {} : { go: "settings" as const, action: "Zu den Einstellungen" }),
    });
  } else if (device.reachability === "overdue") {
    /**
     * **Der Unterschied zum Fall darüber ist der ganze Zweck der Abstufung.**
     * Hier gilt die Verbindung am Broker weiter als bestehend — es ist kein
     * Testament eingegangen —, und trotzdem kommt nichts. Das ist ein hängendes
     * Gerät, ein Netzausfall zwischen beiden oder eine alte behaltene Nachricht
     * nach einem Broker-Neustart. Nur in diesem Fall lohnt der Blick, ob es sich
     * gleich wieder meldet.
     */
    const interval = device.reportedSettings?.cloudInterval;
    hints.push({
      id: "overdue",
      title: "Meldet sich nicht mehr",
      text:
        `Zuletzt gemeldet ${formatTimeAgo(device.lastSeenAt, now)}` +
        `${interval === undefined ? "" : `, bei einem Sendetakt von ${interval} Sekunden`}. ` +
        "Die Verbindung gilt noch als bestehend — es ist kein Abbruch gemeldet worden. Das " +
        "passt zu einem Gerät, das hängt, zu einem unterbrochenen Netz oder zu einem Neustart " +
        "des Brokers. " +
        (kind === "gateway"
          ? "Meldet es sich nicht von selbst, starte das Gateway auf seinem Rechner neu."
          : "Meldet es sich nicht von selbst, hilft ein Neustart des Geräts."),
      ...(kind === "gateway" ? {} : { go: "settings" as const, action: "Zu den Einstellungen" }),
    });
  } else if (device.reachability === "reachable" && device.readingsStalled) {
    /**
     * **Ein Lebenszeichen ist kein Messwert** (HM-179). Der Lesekopf meldet
     * seinen Zustand weiter, liest am Zähler aber nichts — und stand bis dahin
     * dauerhaft als „erreichbar" da, während unter „Strom & Erzeugung" die
     * Werte fehlten. Die Frist rechnet die API (`readingsStalled`); der Hinweis
     * verschwindet mit dem nächsten geschriebenen Wert.
     */
    hints.push({
      id: "readings-stalled",
      title: "Meldet sich, liefert aber keine Werte",
      text:
        `Der letzte Wert kam ${formatTimeAgo(device.lastReadingAt, now)}; das Gerät selbst ` +
        "meldet sich weiter. Meist liest der Lesekopf am Zähler nichts: Er ist verrutscht, die " +
        "Info-Schnittstelle des Zählers ist gesperrt, oder das Kabel zur Zusatzschnittstelle " +
        "steckt nicht. Unter „Einstellungen“ steht, was der Kopf zuletzt am Zähler gelesen hat.",
      go: "settings",
      action: "Zu den Einstellungen",
    });
  }

  const dbm = device.signalStrengthDbm ?? device.diagnostics?.net?.rssi;
  if (dbm !== null && dbm !== undefined && signalQuality(dbm) === "schwach") {
    const ssid = device.diagnostics?.net?.ssid;
    hints.push({
      id: "signal",
      title: "Funksignal schwach",
      text:
        `${dbm} dBm${ssid === undefined ? "" : ` im Netz ${ssid}`}. Unterhalb von −70 dBm ` +
        "brechen Verbindungen ab; ein näherer Zugangspunkt hilft mehr als jede Einstellung.",
    });
  }

  return hints;
}

/** Schweigt das Gerät — überfällig oder nachweislich getrennt? `never_seen` nicht. */
function isSilent(device: Pick<Device, "reachability">): boolean {
  return device.reachability === "overdue" || device.reachability === "unreachable";
}

/**
 * Der Hinweis, wenn der Ingest Nachrichten verwirft — **je Klasse mit dem
 * Grund, den es dort wirklich gibt.** Das Kontingent hängt am Absender auf dem
 * Broker (`MessageGate`): Beim Gateway ist das ein Vorrat für alle Geräte
 * dahinter, und der Rat „Sendetakt in der Konfiguration" hätte dort kein Feld,
 * an dem man ihn befolgen könnte.
 */
/**
 * Wann eine Flut war, so knapp wie es geht: Eine Flut dauert oft Sekunden, und
 * „vom 23. Oktober um 11:37 bis 23. Oktober um 11:37" liest sich wie ein Fehler.
 * Gleiche Minute: ein Zeitpunkt; gleicher Tag: der Tag einmal.
 */
export function floodSpan(from: string, to: string): string {
  const day = (iso: string) =>
    new Intl.DateTimeFormat("de-DE", { day: "numeric", month: "long", year: "numeric" }).format(new Date(iso));
  const time = (iso: string) =>
    new Intl.DateTimeFormat("de-DE", { hour: "2-digit", minute: "2-digit" }).format(new Date(iso));
  if (day(from) !== day(to)) return `vom ${formatDateTime(from)} bis ${formatDateTime(to)}`;
  if (time(from) === time(to)) return `am ${day(from)} um ${time(from)}`;
  return `am ${day(from)} von ${time(from)} bis ${time(to)}`;
}

/**
 * **Eine vergangene Flut** (Befund J2-1). Sie ist oft nach Sekunden vorbei, und
 * mit ihr ging bis dahin die Marke — die Lücke im Verlauf stand ohne Grund da.
 * Ob sie noch erwähnt wird, entscheidet die API (`lastFlood`, eine Woche);
 * hier steht nur der Satz. Kein Knopf: Es ist nichts mehr zu tun, nur zu wissen,
 * warum Werte fehlen.
 */
function pastFloodHint(flood: NonNullable<Device["lastFlood"]>, kind: ReturnType<typeof deviceClass>): Hint {
  const count =
    flood.dropped === null
      ? "eine nicht gezählte Menge Nachrichten"
      : `${flood.droppedAtLeast ? "mindestens " : ""}${flood.dropped.toLocaleString("de-DE")} ${
          flood.dropped === 1 ? "Nachricht" : "Nachrichten"
        }`;
  const who = kind === "gateway" ? "Das Gateway samt der Geräte dahinter" : "Das Gerät";
  return {
    id: "past-flood",
    title: "Hat zeitweise zu viel gesendet",
    text:
      `${who} lag ${floodSpan(flood.from, flood.to)} über seinem ` +
      `Nachrichtenkontingent; ${count} wurden verworfen. Die Werte aus dieser Zeit fehlen. ` +
      "Seither liegt es wieder im Kontingent.",
  };
}

function floodingHint(device: Device, kind: ReturnType<typeof deviceClass>, now?: number): Hint {
  const seit = `Seit ${formatTimeAgo(device.floodingSince, now)} überschreitet es sein Nachrichtenkontingent`;
  const folge =
    "Was darüber liegt, wird verworfen und nicht gespeichert; die Werte bekommen Lücken. " +
    "Sobald es wieder im Kontingent liegt, verschwindet dieser Hinweis von selbst.";

  if (kind === "gateway") {
    return {
      id: "flooding",
      title: "Dieses Gateway sendet mehr, als angenommen wird",
      text:
        `${seit} — es gilt für das Gateway samt aller Geräte dahinter. ${folge} Typische ` +
        "Ursachen: viele zugelassene Geräte mit kurzem Takt oder ein Gerät, das das Gateway " +
        "in einer Schleife abfragt.",
    };
  }

  return {
    id: "flooding",
    title: "Dieses Gerät sendet mehr, als angenommen wird",
    text:
      `${seit} — im Normalbetrieb sendet ein Lesekopf drei bis vier Nachrichten je Minute, und ` +
      `auch eine Nachlieferung nach einem Ausfall passt hinein. ${folge} Typische Ursachen: ein ` +
      "Sendetakt von wenigen Sekunden in der Konfiguration, ein Wackelkontakt am Lesekopf, ein " +
      "Gerät, das in einer Neustartschleife hängt.",
    go: "settings",
    action: "Zur Konfiguration",
  };
}

/**
 * Warum ein Gerät hinter dem Gateway schweigt (HM-113).
 *
 * **Zuerst das Gateway.** Schweigt es, schweigt jedes Gerät dahinter
 * gleichzeitig — derselbe Fall wie die stillstehende Annahmestelle eine Ebene
 * tiefer: Der Hinweis nennt die Ursache und **ersetzt** den zum Gerät, und er
 * führt dorthin, wo sie steht. Ein Neustart-Rat am Gerät ginge an ihr vorbei;
 * einen Knopf dafür gibt es an einem Knoten ohnehin nicht.
 *
 * **Sonst das Gerät — ohne Handlung.** Hier kann die Anwendung nichts tun: Der
 * Weg zwischen Gateway und Gerät liegt im Heimnetz. Und solange das Gateway
 * nicht geladen ist, behauptet der Satz nichts über dessen Zustand.
 */
function nodeSilenceHint(
  device: Device,
  gateway: Device | null | undefined,
  now?: number,
): Hint | null {
  if (gateway !== undefined && gateway !== null && isSilent(gateway)) {
    return {
      id: "gateway-silent",
      title: "Das Gateway meldet sich nicht — nicht dieses Gerät",
      text:
        `Dieses Gerät hat keine eigene Verbindung; seine Werte kommen über „${gateway.name}", ` +
        `und das ist ${REACHABILITY_LABELS[gateway.reachability]} — zuletzt ` +
        `gemeldet ${formatTimeAgo(gateway.lastSeenAt, now)}. Solange es schweigt, erfährt diese ` +
        "Anwendung über das Gerät nichts, auch wenn es selbst in Ordnung ist.",
      route: { kind: "device", siteId: gateway.siteId, deviceId: gateway.id, tab: "overview" },
      action: "Zum Gateway",
    };
  }

  const gatewayOk = gateway !== undefined && gateway !== null;
  const zuletzt = `zuletzt kam ${formatTimeAgo(device.lastSeenAt, now)} etwas an`;

  if (device.reachability === "unreachable") {
    return {
      id: "offline",
      title: "Nicht erreichbar",
      text:
        `Das Gateway meldet dieses Gerät als nicht erreichbar; ${zuletzt}. ` +
        (gatewayOk ? "Das Gateway selbst ist verbunden — unterbrochen ist " : "Unterbrochen ist ") +
        "der Weg zwischen ihm und dem Gerät: Stromversorgung, Netz oder eine geänderte Adresse " +
        "des Geräts.",
    };
  }

  if (device.reachability === "overdue") {
    return {
      id: "overdue",
      title: "Meldet sich nicht mehr",
      text:
        `Von diesem Gerät kommt nichts mehr; ${zuletzt}. Das Gateway hat keinen Ausfall ` +
        "gemeldet — das passt zu einem Gerät, das hängt, oder zu einem Gateway, das es nicht " +
        "mehr abfragt.",
    };
  }

  return null;
}

/**
 * Was das Gateway über ein Gerät dahinter weiß (HM-113) — die Stelle, an der
 * ein Knoten „Hardware und Firmware" stehen hätte.
 *
 * **Nur, was gemeldet wurde** (dieselbe Regel wie in der Knotenzeile der
 * Geräteverwaltung): Ein Modbus-Zähler an Adresse 3 hat weder Seriennummer noch
 * Hersteller, und drei „nicht gemeldet" untereinander sähen aus wie ein Schaden.
 * Der **Schlüssel** steht immer da — er ist die Kennung, unter der das Gerät
 * auch am Gateway steht, und keine Notlösung.
 */
export function nodeRows(
  device: Pick<Device, "gatewayNodeKey">,
  node: GatewayNode | null | undefined,
  now?: number,
): readonly Row[] {
  const { add, rows } = collector();

  add("Schlüssel am Gateway", node?.nodeKey ?? device.gatewayNodeKey ?? undefined);
  add("Gelesen über", textOrNothing(node?.protocol));
  add("Adresse im Netz", textOrNothing(node?.address));
  add("Seriennummer", textOrNothing(node?.serialNumber));
  add("Zuerst gefunden", node == null ? undefined : formatTimeAgo(node.firstSeenAt, now));

  return rows;
}

function textOrNothing(value: string | null | undefined): string | undefined {
  return value === null || value === undefined || value.trim().length === 0 ? undefined : value;
}

/**
 * Ein gemeldeter Konfigurationswert als Text.
 *
 * Wahrheitswerte werden zu „ein"/„aus" und nicht zu „true"/„false": Es ist eine
 * Einstellung, kein Datentyp. Die Zahlen bleiben Zeichenketten, weil das Gerät
 * sie so meldet — die Einheit steht in der Beschriftung.
 */
export function settingValue(value: unknown): string {
  if (typeof value === "boolean") return value ? "ein" : "aus";
  if (Array.isArray(value)) {
    // Ein leerer Eintrag heißt „unbenannt" und nicht „kein Kanal" — die Position
    // in der Liste ist der Kanal, und die darf nicht verrutschen.
    return value.map((entry) => (entry === "" ? "unbenannt" : String(entry))).join(" · ");
  }
  return String(value);
}

/**
 * Sekunden als Laufzeit, ausgeschrieben — immer zwei Einheiten, sobald es mehr
 * als eine gibt: Tage mit Stunden, Stunden mit Minuten.
 *
 * **Die zweite Einheit trägt hier wirklich etwas.** „1 Stunde" stand vorher an
 * allem zwischen einer und zwei Stunden; an einem Gerät, das gerade neu
 * gestartet ist, ist genau das die Frage. Ein glatter Rest fällt weg — „1 Stunde,
 * 0 Minuten" liest sich wie ein Fehler.
 *
 * Sekunden kommen nicht vor: Die Zahl wird alle fünf Minuten gemeldet, und eine
 * Genauigkeit vorzuspiegeln, die die Quelle nicht hat, wäre dieselbe Unehrlichkeit
 * wie ein Platzhalter mit Wert.
 */
export function formatDuration(seconds: number): string {
  const days = Math.floor(seconds / 86_400);
  const hours = Math.floor((seconds % 86_400) / 3_600);
  const minutes = Math.floor((seconds % 3_600) / 60);

  if (days > 0) {
    const tage = `${days} ${days === 1 ? "Tag" : "Tage"}`;
    return hours === 0 ? tage : `${tage}, ${plural(hours, "Stunde")}`;
  }
  if (hours > 0) {
    const stunden = plural(hours, "Stunde");
    return minutes === 0 ? stunden : `${stunden}, ${plural(minutes, "Minute")}`;
  }
  // „0 Minuten" stand hier an jedem Gerät, das gerade angelaufen ist — eine
  // Zahl, die nach einem Messfehler aussieht und nur „noch nicht lange" meint.
  return minutes === 0 ? "unter einer Minute" : plural(minutes, "Minute");
}

/** Ein „1 Stunden" fällt auf und lässt den Rest der Seite unsauber wirken. */
function plural(count: number, singular: string): string {
  return `${count} ${count === 1 ? singular : `${singular}n`}`;
}

/**
 * Der Zustand eines Zählers am Gerät.
 *
 * Die **CRC-Fehler stehen dabei**, sobald es welche gibt: Ein Lesekopf, der
 * schief sitzt, empfängt Telegramme und verwirft sie — von außen sieht das aus
 * wie ein stiller Zähler, und die einzige Spur ist diese Zahl.
 *
 * **Und die verworfenen Register ebenso** (HM-052). Sie sind die Spur eines
 * Verlusts, der sonst gar keine hat: Ein Register, das der Dekoder des Geräts
 * nicht mehr aufnehmen konnte, erscheint nie im Katalog — es fehlt kein Wert,
 * es fehlt der ganze Datenpunkt, und niemand vermisst, was er nie gesehen hat.
 * „Nicht übernommen" und nicht „verloren": Der Wert ist nicht unterwegs
 * abhandengekommen, das Gerät hat ihn gar nicht erst geführt.
 */
/**
 * Der Zustand eines Lesekopfs in Worten (UI-Durchsicht U7). Die Firmware meldet
 * englische Schlüssel; ein unbekannter bleibt stehen, wie er kam — eine neuere
 * Firmware darf einen dazubekommen, und ihn wegzulassen wäre schlechter als ihn
 * roh zu zeigen.
 */
const METER_STATES: Readonly<Record<string, string>> = {
  receiving: "liest",
  searching: "sucht den Zähler",
  ok: "in Ordnung",
  error: "Fehler",
};

function meterDiagnosis(meter: NonNullable<Diagnostics["meters"]>[number]): string {
  const parts = [meter.state === undefined ? "Zustand unbekannt" : (METER_STATES[meter.state] ?? meter.state)];
  if (meter.telegrams !== undefined) parts.push(`${meter.telegrams} Telegramme`);
  if (meter.crcErrors !== undefined && meter.crcErrors > 0) {
    parts.push(`${meter.crcErrors} CRC-Fehler`);
  }
  if (meter.droppedRegisters !== undefined && meter.droppedRegisters > 0) {
    parts.push(`${meter.droppedRegisters} Register nicht übernommen`);
  }
  return parts.join(" · ");
}

function numberOrNothing(value: number | undefined): string | undefined {
  return value === undefined ? undefined : `${value}`;
}

/**
 * Ein Wahrheitswert als Wort — oder gar nichts.
 *
 * `undefined` bleibt `undefined` und wird **nicht** zu „nein": Ein Gerät, das
 * nichts über verfügbare Updates gesagt hat, hat nicht gesagt, dass es keine
 * gibt (Regel 9).
 */
function booleanOrNothing(value: boolean | undefined, yes: string, no: string): string | undefined {
  if (value === undefined) return undefined;
  return value ? yes : no;
}

/**
 * Die Funkfeldstärke als Zahl **und** als Einordnung.
 *
 * Die Schwellen sind die üblichen Faustwerte der WLAN-Praxis, keine Messaussage
 * über dieses Gerät — deshalb steht die Zahl zuerst und die Einordnung dahinter.
 * Ohne sie sagt „−78 dBm" niemandem etwas, der nicht weiß, dass die Skala
 * negativ ist und größer schlechter bedeutet.
 */
export function signalLabel(dbm: number | null | undefined): string | undefined {
  if (dbm === null || dbm === undefined) return undefined;
  return `${dbm} dBm · ${signalQuality(dbm)}`;
}

/**
 * Die Einordnung allein — dieselben Schwellen, an **einer** Stelle.
 *
 * Die Kachel zeigt Zahl und Wort getrennt, die Zeile in der Karte beides in
 * einem. Zwei Schwellenwerte dafür wären zwei Gelegenheiten, sie verschieden
 * zu wählen: Die Kachel stünde auf „gut" und der Hinweis darüber warnte.
 */
export function signalQuality(dbm: number): "gut" | "brauchbar" | "schwach" {
  if (dbm >= -60) return "gut";
  if (dbm >= -70) return "brauchbar";
  return "schwach";
}
